import { PARTS_FISCALES } from './BaremeIRPP';
import {
  ABATTEMENT_FRAIS_PRO_PLAFOND,
  ABATTEMENT_FRAIS_PRO_TAUX,
  PLAFOND_CNAMGS,
  PLAFOND_CNSS,
  TAUX_CFP,
  TAUX_CNAMGS,
  TAUX_CNAMGS_EMP,
  TAUX_CNAMGS_FAM_EMP,
  TAUX_CNAMGS_SAL,
  TAUX_CNSS,
  TAUX_CNSS_PF_EMP,
  TAUX_CNSS_RP_EMP,
  TAUX_CNSS_VID_EMP,
  TAUX_CNSS_VID_SAL,
  TAUX_FGTLS,
  TAUX_FNH,
  TAUX_TCS,
} from './Taux';

export function formatPayrollRate(rate: number): string {
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate * 100)} %`;
}

/** Affichage compact (ex. 5 %) — sidebar / aide, depuis `Taux.ts`. */
export function formatPayrollRateCompact(rate: number): string {
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rate * 100)} %`;
}

/**
 * Libellé sidebar Phase 4 A8 / P4.1 — somme CNSS salarial + patronal depuis `Taux.ts`
 * (5 % + 18 % = **23 %**). Jamais de total hard-codé hors `Taux.ts`.
 */
export function cnssSidebarTauxLabel(): string {
  const total = TAUX_CNSS.SALARIAL + TAUX_CNSS.PATRONAL;
  return `CNSS ${formatPayrollRateCompact(total)}`;
}

/** Pied de sidebar — taux issus uniquement de `Taux.ts` (pas de % hard-codés). */
export function sidebarFiscalFooterLine(): string {
  return `Taux 2026 — ${cnssSidebarTauxLabel()} (${formatPayrollRateCompact(TAUX_CNSS.SALARIAL)}+${formatPayrollRateCompact(TAUX_CNSS.PATRONAL)}) · CNAMGS ${formatPayrollRateCompact(TAUX_CNAMGS.SALARIAL)} / ${formatPayrollRateCompact(TAUX_CNAMGS.PATRONAL)}`;
}

export function formatPayrollAmount(amount: number): string {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
    .format(amount)
    .replaceAll('\u202f', ' ')} FCFA`;
}

export const PAYROLL_PARAMETERS = {
  /** CNSS patronale totale (18 % = 5 % + 2 % + 11 %). */
  cnssEmployerTotal: TAUX_CNSS.PATRONAL,
  /** Prestations familiales et maternité (employeur), 5 %. */
  cnssPfEmployer: TAUX_CNSS_PF_EMP,
  cnssRiskEmployer: TAUX_CNSS_RP_EMP,
  cnssPensionEmployer: TAUX_CNSS_VID_EMP,
  cnssPensionEmployee: TAUX_CNSS_VID_SAL,
  cnamgsEmployer: TAUX_CNAMGS_EMP,
  cnamgsFamilyEmployer: TAUX_CNAMGS_FAM_EMP,
  cnamgsEmployee: TAUX_CNAMGS_SAL,
  cfpEmployer: TAUX_CFP,
  fnhEmployer: TAUX_FNH,
  /** FGTLS patronale 1 % (Loi 3/94). */
  fgtlsEmployer: TAUX_FGTLS,
  /** TCS — retenue salariale (alias historique `tcsEmployer` conservé). */
  tcsEmployee: TAUX_TCS,
  tcsEmployer: TAUX_TCS,
  cnssCeiling: PLAFOND_CNSS,
  cnamgsCeiling: PLAFOND_CNAMGS,
  cnamgsCeilingLabel: Number.isFinite(PLAFOND_CNAMGS)
    ? formatPayrollAmount(PLAFOND_CNAMGS)
    : 'Non plafonné',
  professionalAllowanceRate: ABATTEMENT_FRAIS_PRO_TAUX,
  professionalAllowanceCeiling: ABATTEMENT_FRAIS_PRO_PLAFOND,
  maximumTaxParts: PARTS_FISCALES.MAX_PARTS,
} as const;

const PAYROLL_PARAMETER_TOKENS: Readonly<Record<string, string>> = {
  PAYROLL_CNSS_EMP_TOTAL: formatPayrollRate(PAYROLL_PARAMETERS.cnssEmployerTotal),
  PAYROLL_CNSS_PF_EMP: formatPayrollRate(PAYROLL_PARAMETERS.cnssPfEmployer),
  PAYROLL_CNSS_RISK_EMP: formatPayrollRate(PAYROLL_PARAMETERS.cnssRiskEmployer),
  PAYROLL_CNSS_PENSION_EMP: formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployer),
  PAYROLL_CNSS_PENSION_SAL: formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployee),
  PAYROLL_CNAMGS_EMP: formatPayrollRate(PAYROLL_PARAMETERS.cnamgsEmployer),
  PAYROLL_CNAMGS_FAMILY_EMP: formatPayrollRate(PAYROLL_PARAMETERS.cnamgsFamilyEmployer),
  PAYROLL_CNAMGS_SAL: formatPayrollRate(PAYROLL_PARAMETERS.cnamgsEmployee),
  PAYROLL_CFP_EMP: formatPayrollRate(PAYROLL_PARAMETERS.cfpEmployer),
  PAYROLL_FNH_EMP: formatPayrollRate(PAYROLL_PARAMETERS.fnhEmployer),
  PAYROLL_FGTLS_EMP: formatPayrollRate(PAYROLL_PARAMETERS.fgtlsEmployer),
  PAYROLL_TCS_SAL: formatPayrollRate(PAYROLL_PARAMETERS.tcsEmployee),
  /** @deprecated Alias — préférer PAYROLL_TCS_SAL. */
  PAYROLL_TCS_EMP: formatPayrollRate(PAYROLL_PARAMETERS.tcsEmployee),
  PAYROLL_CNSS_CEILING: formatPayrollAmount(PAYROLL_PARAMETERS.cnssCeiling),
  PAYROLL_CNAMGS_CEILING: PAYROLL_PARAMETERS.cnamgsCeilingLabel,
  PAYROLL_ALLOWANCE_RATE: formatPayrollRate(PAYROLL_PARAMETERS.professionalAllowanceRate),
  PAYROLL_ALLOWANCE_CEILING: formatPayrollAmount(PAYROLL_PARAMETERS.professionalAllowanceCeiling),
  PAYROLL_MAX_TAX_PARTS: PAYROLL_PARAMETERS.maximumTaxParts.toFixed(2).replace('.', ','),
};

export function renderPayrollParameterTokens(template: string): string {
  return template.replace(/\{\{([A-Z0-9_]+)\}\}/g, (token, key: string) => {
    return PAYROLL_PARAMETER_TOKENS[key] ?? token;
  });
}
